import React, {
  ReactEventHandler,
  ChangeEvent,
  KeyboardEvent,
  ReactNode,
} from "react";
import { FieldAttributes } from "formik";
import { Option } from "react-bootstrap-typeahead/types/types";
import {
  QueryObserverResult,
  RefetchOptions,
  UseMutateFunction,
} from "@tanstack/react-query";
import { CSSObject, CSSProperties, DefaultTheme } from "styled-components";
import { TypeaheadComponentProps } from "react-bootstrap-typeahead";

//App
import { UserViewModel } from "../viewModels/userViewModel";
import { UserDto } from "../app/model/api/types/dto/userDto";
import { ApiResponse } from "../app/model/api/types/apiTypes";
import { SearchValue } from "../store/types";
import {
  UnlockUserPayload,
  UserResendAccountConfirmationPayload,
  UserResetPasswordPayload,
} from "./user";
import { LookupDto } from "../app/model/api/types/dto";
import { ColumnDescription } from "react-bootstrap-table-next";
import { OrganizationEmployeeInvalidCombinedDto } from "../app/model/api/types/dto/organizationEmployeeInvalidUploadDto";

export interface Props<T> {
  data?: Array<T> | undefined;
  deleteMutation?: UseMutateFunction<void, Error, string, unknown>;
  activateMutation?: UseMutateFunction<void, Error, string, unknown>;
}

export interface FormViewProps {
  onClose: VoidFunction;
  editAction: VoidFunction;
  toggleDeactivateHandler?: VoidFunction;
  toggleActivateHandler?: VoidFunction;
  initialVal?: UserViewModel;
  toggleResetPasswordHandler?: VoidFunction;
}

export interface FormContainerProps {
  onClose: VoidFunction;
  onShowSuccess: (message: string) => void;
}

export interface UserFormContainerProps {
  organizationId?: string;
  onClose: VoidFunction;
  onShowSuccess: (message: string) => void;
}

export interface FormProps<T> {
  initialVal: T;
  status: string;
  handleOnSubmit: (values: T) => Promise<void>;
  onClose: () => void;
}

export interface SnackbarSuccessState {
  showSuccessSnackbar: boolean;
  successMessage: string;
}

export interface FormState extends SnackbarSuccessState {
  toggleNewForm: boolean;
  toggleEditForm: boolean;
  toggleView: boolean;
  showErrorSnackbar: boolean;
  isActivateDialogOpen: boolean;
  isDeactivateDialogOpen: boolean;
}

export interface InvalidFormState extends SnackbarSuccessState {
  toggleView: boolean;
  showErrorSnackbar: boolean;
  formData: OrganizationEmployeeInvalidCombinedDto | null;
}

export const defaultInvalidFormState: InvalidFormState = {
  toggleView: false,
  showErrorSnackbar: false,
  showSuccessSnackbar: false,
  successMessage: "",
  formData: null,
};

export interface FormContextDataProps<T, U = T> {
  data: T | undefined;
  id: string | null;
  activeToggle?: boolean;
  searchValue?: SearchValue;
  pageSize?: number;
  paging?: {
    totalSize: number;
    currentPage: number;
  };
  setId: (id: string | null) => Promise<void>;
  refetchGrid: (
    options?: RefetchOptions | undefined
  ) => Promise<QueryObserverResult<ApiResponse<U[]>, Error>>;
  setActiveToggleValue?: () => Promise<void>;
  setCurrentPage?: (currentPage: number) => Promise<void>;
  setCurrentPageSize?: (pageSize: number) => Promise<void>;
  setSearchValue?: (values: SearchValue | null) => Promise<void>;
  setSortBy?: (sortBy: SortBy) => Promise<void>;
}

export const defaultFormState: FormState = {
  toggleNewForm: false,
  toggleEditForm: false,
  toggleView: false,
  showSuccessSnackbar: false,
  showErrorSnackbar: false,
  isActivateDialogOpen: false,
  isDeactivateDialogOpen: false,
  successMessage: "",
};

export type FormikTypeaheadOption = Option;

export interface Options {
  label: string;
  value: string;
  children?: ReactNode;
}

export interface ButtonProps {
  className?: string;
  style?: CSSProperties;
  color?: string;
  variant?: string;
  disabled?: boolean;
  children: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

export interface InputProps {
  id?: string;
  label?: string;
  name: string;
  isRequired?: boolean;
  disabled?: boolean;
  labelStyle?: CSSObject;
  labelClassName?: string;
  includeOther?: boolean;
  includeAfterOther?: any;
}

export interface FormikTypeaheadProps
  extends InputProps,
    TypeaheadComponentProps {
  groupByKey?: string;
  ref?: any;
}

export interface CheckboxProps extends InputProps {
  columns?: number;
  options?: Array<Options>;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export interface CheckboxListProps extends InputProps {
  columns?: number;
  options?: Array<Options>;
  onChange?: (values: Array<string>) => void;
}

export interface DropdownProps extends InputProps {
  options: Array<Options>;
  style?: CSSObject;
  insertDefault?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export interface TextFieldProps extends InputProps {
  type?: "text" | "textarea" | "password" | "number" | "datetime" | "time";
  prepend?: string;
  style?: CSSObject;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEventHandler<HTMLInputElement>) => void;
  ignoreFieldChangeOnEmpty?: boolean;
  decimalPlace?: number;
  isDollar?: boolean;
}

export interface RadioGroupProps extends InputProps {
  options: Array<Options>;
  columns?: number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export interface DrawerProps {
  show: boolean;
  fullWidth?: boolean;
  children: React.ReactNode;
}

export interface CreateTextMaskCustomProps {
  mask: Array<string | RegExp>;
}

export interface MaskedTextboxProps extends FieldAttributes<any> {
  label?: string;
  placeholder?: string;
  maskedfield: React.ComponentType<any>;
  variant?: string;
  name: string;
  value?: string;
  isRequired?: boolean;
}

export interface ConfirmationDialogProps {
  contentText: string;
  contentTitle: string;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  closeText?: string;
  confirmText?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  confirmOnly?: boolean;
  size?: string;
}

export interface UpdateTotalDialogProps {
  open: boolean;
  children?: React.ReactNode;
  onClose: () => void;
  onConfirm: () => void;
  disabled?: boolean;
  dialogTitle: string;
  saveText: string;
  errorText?: string;
  fullscreen?: boolean;
}

export interface PageContentProps {
  children: React.ReactNode;
}

export interface ContainerContentProps {
  fullscreen?: boolean;
}

export interface NewButtonProps {
  style?: CSSObject;
  toggleNewViewHandler: VoidFunction;
}

export interface StylesProps {
  theme: DefaultTheme;
}

export interface DataGridProps {
  columns: ColumnDescription[] | any;
  data: any;
  noDataMessage?: string;
  keyField?: string;
  totalSize?: number;
  isExpandableRow?: boolean;
  isRowSelectable?: boolean;
  onSelectionChange?: (selectedRows: any[]) => void;
  expandRowData?: any;
  rowEvents?: any;
  remote?: boolean;
  page?: number;
  pageSize?: number;
  ref?: any;
  defaultSorted?: any;
  handlePageChange?: (
    pageNumber: number,
    pageSize: number,
    sortField?: string,
    sortOrder?: string
  ) => void;
}

export interface FormHeaderProps {
  title?: string | null;
  subTitle?: string | null;
  chipLabel?: string;
  onClose: VoidFunction;
  editActionHandler?: VoidFunction;
  formClassName?: string;
}

export interface UserFormProps {
  handleOnSubmit: (values: UserViewModel) => Promise<void>;
  onClose: VoidFunction;
  initialVal: UserViewModel;
  status: string;
  lookups?: Array<LookupDto> | undefined;
}

export interface UserProps {
  organizationId?: string;
  users?: Array<UserDto> | undefined;
  deleteMutation: UseMutateFunction<void, Error, string, unknown>;
  activateMutation: UseMutateFunction<void, Error, string, unknown>;
  resendConfirmationEmail: UseMutateFunction<
    boolean,
    Error,
    UserResendAccountConfirmationPayload,
    unknown
  >;
  unlockUserMutation: UseMutateFunction<
    void,
    Error,
    UnlockUserPayload,
    unknown
  >;
  resetPasswordMutation: UseMutateFunction<
    void,
    Error,
    UserResetPasswordPayload,
    unknown
  >;
  fullscreen?: boolean;
}

export interface ChangePasswordProps {
  users?: Array<UserDto> | undefined;
  deleteMutation: UseMutateFunction<void, Error, string, unknown>;
  activateMutation: UseMutateFunction<void, Error, string, unknown>;
}

export interface UserViewProps extends FormViewProps {}

export interface SnackbarProps {
  open: boolean;
  message: string | null;
  severity: "success" | "danger";
  handleClose: () => void;
}

export interface DatePickerProps extends InputProps {
  value?: string | undefined;
  minDate?: string | undefined;
  maxDate?: string | undefined;
  placeholder?: string;
  type?: "date" | "time" | "datetime-local";
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export interface IconProps {
  iconText?: string;
  pendingText?: string;
}

export interface MoreActionsMenuProps {
  isOpen: boolean;
  target: HTMLElement;
  viewIconText?: string;
  viewIconPendingText?: string;
  children?: React.ReactNode;
  toggle: VoidFunction;
  onViewClick?: VoidFunction;
}

export interface RoleMoreActionsMenuProps extends MoreActionsMenuProps {
  onPermissionClick?: VoidFunction;
}

export interface UserMoreActionsMenuProps extends MoreActionsMenuProps {
  onPermissionClick?: VoidFunction;
  onResendVerificationClick?: VoidFunction;
  onUnlockUserClick?: VoidFunction;
  onResetPasswordClick?: VoidFunction;
}

export interface OrganizationMoreActionsMenuProps extends MoreActionsMenuProps {
  onAddUserClick?: VoidFunction;
}

export interface DisplayDataProps {
  text?: string;
  importedData: string | undefined | null | any;
  hideColon?: boolean;
  isBold?: boolean;
  verticalCenter?: boolean;
}

export interface AddressDataProps {
  text?: string;
  AddressLine1: string | undefined | null;
  AddressLine2: string | undefined | null;
  City: string | undefined | null;
  State: string | undefined | null;
  ZipCode: string | undefined | null;
  County?: string | undefined | null;
  hideColon?: boolean;
  isBold?: boolean;
}

export interface EditButtonProps {
  text?: boolean;
  editActionHandler: VoidFunction;
}

export interface ActionBarProps {
  submitText?: string;
  displayText?: ReactNode;
  displayTextStyle?: CSSObject;
  cancelText?: string;
  submitDisabled?: boolean;
  isLoading?: boolean;
  customImageButton?: string;
  submitButtonLoading?: boolean;
  deleteHandler?: ReactEventHandler;
  cancelHandler?: ReactEventHandler;
  editHandler?: ReactEventHandler;
  submitHandler?: ReactEventHandler;
}

export interface LoadingIndicatorProps {
  notFullPage?: boolean;
  size?: string;
  style?: CSSProperties;
}

export interface SnackbarState {
  showErrorSnackbar: boolean;
  errorMessage: string;
}

export interface ToggleButtonProps {
  style?: CSSObject;
  labelText: string;
  defaultChecked: boolean;
  handleChange: VoidFunction;
}

export interface ChipProps {
  label: string;
  style?: CSSObject;
}

export interface BaseChipProps extends ChipProps {
  colortext?: string;
  backgroundcolor?: string;
  style?: CSSObject;
}

export interface SearchFields {
  name: boolean;
  personnelNumber: boolean;
  contributorType?: boolean;
  agency?: boolean;
  responded?: boolean;
  hasContributed?: boolean;
  methodOfGiving?: boolean;
}

export interface SearchProps<T> {
  dropdown?: any;
  contributorTypes?: FormikTypeaheadOption[];
  agencies?: FormikTypeaheadOption[];
  methodOfGivingTypes?: FormikTypeaheadOption[];
  wrapFiltersEarly?: boolean;
  breakActionsToNewRow?: boolean;
  filterLayout?: "campaign-contributions" | "campaign-designations";
  searchValue: SearchValue;
  searchFields: SearchFields;
  setSearchValue: ((values: SearchValue | null) => Promise<void>) | undefined;
  refetchGrid:
    | ((
        options?: RefetchOptions | undefined
      ) => Promise<QueryObserverResult<ApiResponse<T[]>, Error>>)
    | undefined;
}

export interface SearchContainerProps<T> {
  searchProps?: SearchProps<T>;
  activeToggle?: boolean;
  newButtonStyle?: CSSObject;
  handleActiveToggleChange?: VoidFunction;
  toggleNewViewHandler?: VoidFunction;
}

export interface SortBy {
  field: string;
  direction: number;
}

export interface ResendVerificationProps {
  isResendVerification: boolean;
  userName: string;
  handleOnClose: VoidFunction;
  handleOnConfirm: VoidFunction;
}

export interface PayrollFrequencyProps {
  payrollFrequencyOptions: Array<Options>;
}

export interface ExportButtonProps<T = any> {
  style?: CSSObject;
  url: string;
  method?: "GET" | "POST";
  fileName?: string;
  dataDownload?: boolean;
  postData?: T;
  text?: string;
}
